// fw PhoneShell — the frame for Field and Dispatch.
//
// Status strip is the browser's. Then: a header with an uppercase condensed
// eyebrow and a condensed 26px title; an optional banner slot (the offline
// notice); a scrollable body; a fixed bottom TabBar with exactly the tabs
// given. Safe-area insets are respected top and bottom.

import React, { useEffect, useRef } from 'react';
import { TabBar } from './TabBar.js';
import '../tokens.css';
import '../base.css';
import './PhoneShell.css';

/**
 * Publish the tab bar's REAL height as `--fw-bottom-bar-gap` on the shell.
 *
 * ── Why this is measured and not a token ──────────────────────────────────
 *
 * `.fw-tabbar` sets `min-height: var(--fw-tabbar-h)`. A minimum is not a
 * height: the bar is as tall as its content needs, and two engines lay that
 * content out differently. The CSS fallback in PhoneShell.css assumes they
 * agree. They do not.
 *
 * MEASURED on the first WebKit run in CI, 2026-09-22, `crew-job-clock` at
 * 390x844 — the crew member's clock-in screen:
 *
 *     Clock in   bottom 772        tab bar   top 769
 *
 * Three pixels of the button behind the bar, so the bottom of the primary
 * action on the primary screen is not tappable. The same screen is clean in
 * Chromium, where the bar comes out at the token's 64px; in WebKit it came out
 * 75px — WHICH TURNED OUT TO BE WRONG, and is corrected below. Every unit
 * test passed, and so did 3025 Chromium browser checks.
 *
 * ── The measured value still carries the home-indicator inset ─────────────
 *
 * The CSS fallback is `calc(var(--fw-tabbar-h) + env(safe-area-inset-bottom))`
 * and this replaces it with a bare `offsetHeight`, so the obvious way for this
 * to be wrong is to drop ~34px on a notched iPhone and put `Clock in` back
 * into the home indicator. It does not: `TabBar.css:5` puts
 * `padding-bottom: env(safe-area-inset-bottom, 0px)` on `.fw-tabbar`, which is
 * INSIDE the `.fw-phone__tabbar` wrapper this measures, so the inset is part
 * of `offsetHeight` — along with the 1px border the fallback never counted.
 *
 * Stated here because no automated check in this repo can catch it if it
 * breaks: headless resolves the inset to 0 in BOTH engines. Measured instead
 * by rewriting the inset to a real 34px in the served stylesheet — bar 99px,
 * old calc 98px, published gap 99px.
 *
 * Guarded on `ResizeObserver` because jsdom has none — the same guard and the
 * same reason as `useNarrowContainer` in RecordList.js, which is the pattern
 * this follows deliberately rather than inventing a second one. Without a
 * ResizeObserver nothing is written and the CSS fallback stands, which is the
 * behaviour every screen had before this.
 */
/**
 * The most a real bottom bar's TOP can sit above the viewport floor, over and
 * above the bar's own height — the width of the sanity band in `publish()`.
 *
 * It is `--fw-tabbar-h`, and it used to be the literal `64` written out
 * longhand where the band is computed. Change the token and the band would
 * have drifted silently, with nothing pinning the two together. Raised in
 * review on #690; `phoneBarHeight.test.js` now asserts this equals the token
 * in tokens.css, so they cannot separate.
 *
 * Why a bar height is the right size for the band: WebKit puts the bar's
 * bottom 10px above the floor on a 390x844 phone (measured — that is the
 * whole reason this publishes a reach), and the band has to be loose enough
 * to admit that while still refusing a bar that is not laid out at all, or
 * one somebody has moved into the middle of the screen.
 */
export const MAX_BAR_LIFT_PX = 64;

/** Writes allowed in one burst before the loop guard gives up (see publish). */
const LOOP_WRITE_BUDGET = 20;
/** Quiet time that refills that budget, ms. */
const LOOP_WINDOW_MS = 1000;
/**
 * How long the give-up path waits before trying again.
 *
 * STRICTLY LONGER than the window, and the `+ 1` is load-bearing rather than
 * defensive. The budget resets on `now - lastWriteAt > LOOP_WINDOW_MS`, and
 * `lastWriteAt` is refreshed on every ATTEMPT — so re-arming at exactly
 * `LOOP_WINDOW_MS` lets the retry land with a delta of exactly the threshold,
 * which is not greater than it, and the retry refuses itself. It recovered
 * anyway, because `setTimeout` overshoots and a frame is added on top — i.e.
 * it was correct by luck. Raised in review on #690.
 */
const LOOP_RETRY_MS = LOOP_WINDOW_MS + 1;

function usePublishedBarHeight(shellRef, barRef) {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const shell = shellRef.current;
    const bar = barRef.current;
    if (!shell || !bar) return undefined;

    // WRITE ONLY ON CHANGE. Setting a custom property inside a
    // ResizeObserver callback changes layout, which schedules another
    // callback, which writes again: WebKit answers that with
    //
    //     ResizeObserver loop completed with undelivered notifications.
    //
    // — a real console error, on a real screen, introduced by the first
    // version of this fix and caught by the WebKit run on the `tab-bar`
    // scenario. Worse than the noise: WebKit ABANDONS the remaining
    // notifications, so the write can be the one that never lands, and the
    // measured height silently stays unapplied. `crew-job-clock` was still
    // 2px behind the bar in that same run, with the gap sitting at the
    // token's 64px rather than the measured value.
    //
    // Chromium coalesces this quietly, which is why it has to be stated here
    // rather than discovered again.
    let published = null;
    // ── AND A BOUND, because the rAF fix removed the only alarm ──────────
    //
    // Raised in review on #690, and the reasoning is right. `published` only
    // refuses an IDENTICAL value, so an oscillation between two values was
    // never blocked — it was AUDIBLE, as WebKit's "ResizeObserver loop
    // completed with undelivered notifications". Deferring the write to the
    // next frame stops that error being raised, which means a feedback path
    // that still existed would now be silent: one write per frame, forever,
    // on the crew's primary screen.
    //
    // No oscillation is known. The review could not produce one and neither
    // could I, and the most obvious candidate does not work — a scrollbar
    // appearing moves `clientHeight` and the fixed bar's `rect.top` by the
    // same amount, so `toFloor` comes out unchanged. This is not a fix for a
    // bug that has been seen; it is refusing to trade a loud failure for a
    // quiet one.
    //
    // A burst-and-reset counter rather than a lifetime cap: rotation, the
    // iOS keyboard and a standalone-mode transition all legitimately
    // republish several times in quick succession, and a lifetime cap would
    // eventually stop serving them. Quiet for LOOP_WINDOW_MS and the budget
    // is back, so the worst case degrades to "stop fighting for a second",
    // never to "stop working".
    let writes = 0;
    // Refreshed on every ATTEMPT, not only on writes — deliberately. A live
    // oscillation must never refill its own budget by being refused, so the
    // quiet window is measured from the last time the shell TRIED to publish.
    // (The comment above said "quiet for LOOP_WINDOW_MS", which is a slightly
    // different guarantee; raised in review on #690 and worth stating exactly.)
    let lastWriteAt = 0;
    let loopReported = false;
    // Set once `schedule` exists. The give-up path below needs to re-arm, and
    // `schedule` is declared after this — in the no-ResizeObserver branch it
    // is never declared at all, so this cannot be a bare reference.
    let scheduleRef = null;
    let retryTimer = null;
    const publish = () => {
      // ── THE DISTANCE TO THE BAR'S TOP, NOT THE BAR'S HEIGHT ──────────────
      //
      // These are the same number in Chromium and they are NOT in WebKit, and
      // the difference is the 2px of `Clock in` that stayed under the tab bar
      // after the first fix. Measured in CI, crew-job-clock at 390x844:
      //
      //   published 65px · wrapper 65 (rect 65) · nav 65 (rect 65)
      //   bar top 769 · sticky bottom 73px · button bottom 771
      //
      // The bar really is 65 tall and the shell measured it correctly. But a
      // bar 65 tall whose top is at 769 has its BOTTOM at 834 — ten pixels
      // above the 844 viewport floor. `position: fixed; bottom: 0` and
      // `position: sticky; bottom: N` are resolving against different bottoms,
      // which is the same 10px that shows up as `visualViewport 310` against a
      // `clientWidth` of 320 elsewhere in the same run.
      //
      // So publishing the HEIGHT was always answering the wrong question. What
      // a `bottom:`-anchored sticky needs is how far off the viewport floor it
      // must sit to clear the bar, and that is `viewportHeight - barTop`.
      // Chromium: 844 - 779 = 65, exactly what it published before, so nothing
      // moves there. WebKit: 844 - 769 = 75, which is the number that clears.
      // Engine-agnostic by construction rather than by luck.
      const rect = bar.getBoundingClientRect();
      const viewportH = document.documentElement.clientHeight;
      const toFloor = Math.round(viewportH - rect.top);
      // A bar that is not laid out, or one somebody has moved off the bottom,
      // must not publish a gap the size of the screen. The reach can never be
      // less than the bar's own height, and a bottom bar is never more than a
      // bar-height above the floor, so anything outside that band is not a
      // measurement of a bottom bar and the height is the safer answer.
      const height = Math.round(rect.height);
      const sane = toFloor >= height && toFloor <= height + MAX_BAR_LIFT_PX;
      const h = sane ? toFloor : height;
      // A bar that has not been laid out reports 0. Writing that would put
      // every sticky back on the floor of the viewport, which is the defect
      // this exists to prevent — so leave the CSS fallback in place instead.
      //
      // This deliberately does NOT distinguish "not laid out yet" from "there
      // is no bar". Today it cannot arise — TabBar always renders its `<nav>`
      // and TabBar.css gives it `min-height: var(--fw-tabbar-h)`, so a real
      // browser never reports 0 — and holding the fallback is the safe answer
      // to both. If TabBar ever returns null for an empty `items`, the shell
      // would reserve a bar's height for a bar that is not there; the case in
      // phoneBarHeight.test.js is what would catch that. (Review, #690.)
      // `Number.isFinite`, not `typeof h === 'number'`: NaN IS a number and
      // `NaN <= 0` is false, so a NaN would pass both halves of the old test
      // and write `--fw-bottom-bar-gap: NaNpx`. A custom property stores that
      // happily, and then invalidates `calc()` at computed-value time at both
      // use sites — `padding-bottom` drops to 0 and `bottom:` to `auto`, which
      // IS the defect this whole change exists to fix, arriving by a different
      // door. No reachable path is known (a real browser's rect and
      // `clientHeight` are finite), so this is hardening — but `panAllowance`
      // in this same PR already guards exactly this way, and two spellings of
      // one rule is how they drift. (Review, #690.)
      if (!Number.isFinite(h) || h <= 0) return;
      if (h === published) return;
      const now = Date.now();
      if (now - lastWriteAt > LOOP_WINDOW_MS) writes = 0;
      lastWriteAt = now;
      writes += 1;
      if (writes > LOOP_WRITE_BUDGET) {
        // Once per mount. The point is that this is FINDABLE — in a console,
        // in a ui-check "no page errors" run — rather than a screen quietly
        // writing a custom property sixty times a second.
        if (!loopReported) {
          loopReported = true;
          // eslint-disable-next-line no-console
          console.warn(
            '[PhoneShell] --fw-bottom-bar-gap is oscillating; giving up for now. '
            + `Last values: ${published} -> ${h}. This is a layout feedback loop, not a bar that moved.`);
        }
        // ── AND RE-ARM, or giving up can strand the WRONG value ───────────
        //
        // Raised in review on #690, and it is a defect I introduced in the
        // guard I added to answer the previous review. Returning here without
        // rescheduling means: if the value that arrives once the budget is
        // spent is the SETTLED one, the layout stops producing observer
        // callbacks and `--fw-bottom-bar-gap` stays at whatever intermediate
        // number was written 20 attempts ago.
        //
        // That is the one path in this whole effect where the CSS fallback
        // does NOT catch the shell, because a stale inline value is still set
        // on `.fw-phone` and an inline property beats the stylesheet. Every
        // other failure branch degrades to the fallback; this one degrades to
        // a wrong number, on the clock-in screen.
        //
        // One timer, re-armed only when there is not one pending, so a burst
        // cannot queue dozens. It fires after the quiet window, by which time
        // `writes` has reset and a genuine settled value publishes normally.
        if (scheduleRef && retryTimer === null) {
          retryTimer = setTimeout(() => { retryTimer = null; if (scheduleRef) scheduleRef(); }, LOOP_RETRY_MS);
        }
        return;
      }
      published = h;
      shell.style.setProperty('--fw-bottom-bar-gap', `${h}px`);
    };

    const RO = window.ResizeObserver;
    if (typeof RO !== 'function') { publish(); return undefined; }

    // ── WRITE ON THE NEXT FRAME, NOT INSIDE THE CALLBACK ─────────────────
    //
    // MEASURED: publishing the reach instead of the height closed the
    // clock-in overlap (button bottom 761 against a bar top of 769, 8px clear)
    // and introduced three of these on the same run:
    //
    //     ResizeObserver loop completed with undelivered notifications.
    //
    // — on crew-job-clock, worker-phone and worker-creates-job, where there
    // were none before. It is a loop I created: the write changes
    // `--fw-bottom-bar-gap`, which changes `.fw-phone__body`'s padding, which
    // changes the document height, which can toggle a scrollbar, which moves
    // the viewport, which changes `viewportHeight - barTop`. The `published`
    // equality guard cannot stop that — it only refuses an IDENTICAL value,
    // and an oscillation between two values is not identical.
    //
    // A height could not do this. A reach is measured against the viewport, so
    // it is reflexive by construction, and the standard answer applies: leave
    // the observation cycle before writing. One frame is enough, and it is the
    // documented remedy for this exact console error.
    //
    // `cancelAnimationFrame` on the pending one, so a burst of callbacks
    // writes once rather than queueing a frame each.
    let frame = null;
    const schedule = () => {
      const raf = window.requestAnimationFrame;
      if (typeof raf !== 'function') { publish(); return; }
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = raf(() => { frame = null; publish(); });
    };

    scheduleRef = schedule;
    const ro = new RO(schedule);
    // BORDER box rather than the default content box — and the honest reason
    // is that it costs nothing, NOT the one first written here.
    //
    // That note argued the inset lives in `.fw-tabbar`'s `padding-bottom`, so
    // an inset-only change moves the border box and leaves the content box
    // alone, and a default observer would never fire. True of `.fw-tabbar`.
    // Not true of the element on this line: `barRef` is `.fw-phone__tabbar`,
    // the WRAPPER, which has no padding and no border (PhoneShell.css), so its
    // content box IS its child's border box and a default observer would have
    // fired perfectly well.
    //
    // Corrected rather than deleted because the correction is the useful part:
    // two reviews raised it, I agreed with the second and left the wrong note
    // standing anyway, and CLAUDE.md is explicit that a note which has quietly
    // become false is worse than none because it gets followed.
    //
    // It stays because observing the border box of a wrapper that has no
    // border is exactly equivalent and survives somebody later giving the
    // wrapper padding. Rotation was never affected either way: the bar is
    // `left: 0; right: 0`, so its width changes and the callback fires.
    // ── WHAT THIS OBSERVER DOES NOT SEE, said out loud ───────────────────
    //
    // Raised in review on #690, and it is the assumption the whole fix rests
    // on, so it is written down rather than left implicit.
    //
    // `toFloor` is `documentElement.clientHeight - rect.top` — a function of
    // the LAYOUT VIEWPORT as well as the bar. This observer watches only the
    // bar. So a viewport change that does not resize the bar — an iOS toolbar
    // collapsing as the page scrolls, the soft keyboard opening — does not
    // re-publish, and the gap can go stale.
    //
    // The reason that is believed benign, stated so it can be checked rather
    // than assumed: the bar is `position: fixed; bottom: 0`, so it MOVES with
    // the viewport. Both `clientHeight` and `rect.top` shift by the same
    // amount and their difference is close to invariant. The CSS fallback is
    // the floor underneath either way, and the sanity band refuses anything
    // outside one bar-height of the floor.
    //
    // NOT verified on hardware, and deliberately not "fixed" by adding a
    // `visualViewport` resize listener: on iOS that fires continuously during
    // a scroll, and trading a stale value nobody has seen go wrong for
    // continuous republishing on the crew's primary screen is not a trade to
    // make without a measurement. If a stale gap is ever observed, the
    // listener is the fix and the burst budget above already bounds its cost.
    ro.observe(bar, { box: 'border-box' });
    // The FIRST publish stays synchronous: it runs in the effect, not in an
    // observer callback, so it cannot be the thing that loops — and deferring
    // it would leave every screen on the CSS fallback for a frame.
    publish();
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      if (retryTimer !== null) clearTimeout(retryTimer);
      scheduleRef = null;
      ro.disconnect();
    };
  }, [shellRef, barRef]);
}

export function PhoneShell({ eyebrow, title, tabs = [], activeTab, onTabChange, banner, headerRight, children, className = '', ...rest }) {
  const shellRef = useRef(null);
  const barRef = useRef(null);
  usePublishedBarHeight(shellRef, barRef);

  return (
    <div ref={shellRef} className={['fw-app', 'fw-phone', className].filter(Boolean).join(' ')} data-layout="phone" {...rest}>
      <header className="fw-phone__header">
        <div className="fw-phone__heading">
          {eyebrow ? <div className="fw-phone__eyebrow">{eyebrow}</div> : null}
          {title ? <h1 className="fw-phone__title">{title}</h1> : null}
        </div>
        {headerRight ? <div className="fw-phone__header-right">{headerRight}</div> : null}
      </header>
      {banner ? <div className="fw-phone__banner">{banner}</div> : null}
      <main className="fw-phone__body">{children}</main>
      <div className="fw-phone__tabbar" ref={barRef}>
        <TabBar items={tabs} activeKey={activeTab} onChange={onTabChange} />
      </div>
    </div>
  );
}

export default PhoneShell;
